import { useState } from "react";
import {
  api,
  type GeneratePreviewsResult,
  type StoredPreview,
} from "../app/api";
import { useAsync } from "../app/useAsync";
import { StatusChip } from "../components/StatusChip";

type Entity = "club" | "event" | "workout";

export function MigrationPreviewPage() {
  const [reloadKey, setReloadKey] = useState(0);
  const summary = useAsync(api.previewsSummary, [reloadKey]);
  const [entity, setEntity] = useState<Entity>("club");
  const [limit, setLimit] = useState(10);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<GeneratePreviewsResult | null>(null);
  const [rows, setRows] = useState<StoredPreview[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function generate() {
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      const res = await api.generatePreviews({ entity_type: entity, limit });
      if (!res.ok) setErr(res.error ?? "generation failed");
      setResult(res);
      const list = await api.listPreviews(entity, 25);
      setRows(list.previews);
      setReloadKey((k) => k + 1);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function view() {
    setErr(null);
    try {
      const list = await api.listPreviews(entity, 25);
      setRows(list.previews);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  const s = summary.data?.source;

  return (
    <>
      <h1>Migration Preview</h1>
      <p className="page-sub">
        Dry-run metaobject payloads (exactly what would be sent to Shopify).
        <strong> No writes occur</strong> — payloads are stored in the migration
        database only.
      </p>

      <div className="grid">
        <div className="card">
          <h3>Clubs Ready</h3>
          <div className="stat">{s ? s.clubs : "…"}</div>
          <p className="muted">→ sandlot_ruck_club</p>
        </div>
        <div className="card">
          <h3>Events Ready</h3>
          <div className="stat">{s ? s.events : "…"}</div>
          <p className="muted">→ sandlot_event</p>
        </div>
        <div className="card">
          <h3>Workouts Ready</h3>
          <div className="stat">{s ? s.workouts : "…"}</div>
          <p className="muted">→ sandlot_workout</p>
        </div>
        <div className="card">
          <h3>Previews Stored</h3>
          <div className="stat">{summary.data?.previewsStored ?? "…"}</div>
          <p className="muted">in migration_previews</p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: "1.2rem" }}>
        <h3>Generate previews</h3>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <select
            value={entity}
            onChange={(e) => setEntity(e.target.value as Entity)}
            style={selectStyle}
          >
            <option value="club">club</option>
            <option value="event">event</option>
            <option value="workout">workout</option>
          </select>
          <input
            type="number"
            min={1}
            max={200}
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            style={{ ...selectStyle, width: 90 }}
          />
          <button onClick={generate} disabled={busy} style={btnStyle}>
            {busy ? "Generating…" : "Generate"}
          </button>
          <button onClick={view} style={{ ...btnStyle, background: "var(--surface-2)", color: "var(--ink)" }}>
            View stored
          </button>
        </div>
        {err && <p className="muted" style={{ marginTop: 10 }}>{err}</p>}
        {result?.ok && (
          <p style={{ marginTop: 12 }}>
            <StatusChip tone="ok" label={`${result.generated} generated`} />{" "}
            {result.withWarnings ? (
              <StatusChip tone="warn" label={`${result.withWarnings} with warnings`} />
            ) : null}{" "}
            {result.errors ? (
              <StatusChip tone="no" label={`${result.errors} errors`} />
            ) : null}
          </p>
        )}
        {result?.sample && (
          <details open style={{ marginTop: 12 }}>
            <summary className="muted">Sample payload: {result.sample.handle}</summary>
            {result.sample.warnings.length > 0 && (
              <ul className="muted" style={{ fontSize: "0.82rem" }}>
                {result.sample.warnings.map((w, i) => (
                  <li key={i}>⚠ {w}</li>
                ))}
              </ul>
            )}
            <pre style={preStyle}>{JSON.stringify(result.sample.input, null, 2)}</pre>
          </details>
        )}
      </div>

      {rows && (
        <div className="card">
          <h3>Stored previews ({entity})</h3>
          {rows.length === 0 ? (
            <p className="muted">None yet.</p>
          ) : (
            rows.map((r) => (
              <details key={r.id} style={{ borderBottom: "1px solid var(--hair)", padding: "6px 0" }}>
                <summary>
                  <code>{r.shopifyHandle}</code>{" "}
                  <span className="muted" style={{ fontSize: "0.8rem" }}>
                    {r.status}
                  </span>
                </summary>
                <pre style={preStyle}>{prettyJson(r.payloadJson)}</pre>
              </details>
            ))
          )}
        </div>
      )}
    </>
  );
}

function prettyJson(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}

const selectStyle: React.CSSProperties = {
  padding: "0.5rem 0.7rem",
  background: "var(--surface)",
  border: "1px solid var(--hair)",
  borderRadius: 8,
  color: "var(--ink)",
};
const btnStyle: React.CSSProperties = {
  padding: "0.5rem 1rem",
  background: "var(--accent)",
  color: "#12140f",
  border: "none",
  borderRadius: 8,
  fontWeight: 600,
  cursor: "pointer",
};
const preStyle: React.CSSProperties = {
  overflowX: "auto",
  fontSize: "0.78rem",
  background: "var(--surface-2)",
  padding: "0.9rem",
  borderRadius: 8,
};
